// Loaded by 13 sections, so this file can execute several times on one page.
// The marker keeps a single observer and a single set of listeners.
if (!document.documentElement.dataset.accordionsBound) {
  document.documentElement.dataset.accordionsBound = 'true';

  // Tracked on the node itself, not in a data attribute: a DOM morph that
  // preserves the trigger strips any attribute absent from the server markup,
  // which would let init stack a second click listener on the same trigger —
  // making every click toggle twice and appear not to close.
  const initializedTriggers = new WeakSet();

  const openAccordionContent = (content) => {
    content.style.maxHeight = `${content.scrollHeight}px`;

    content.addEventListener(
      'transitionend',
      (event) => {
        const stillOpen = content.getAttribute('aria-hidden') === 'false';
        if (event.target === content && event.propertyName === 'max-height' && stillOpen) {
          content.style.maxHeight = 'none';
          content.style.overflow = 'visible';
        }
      },
      { once: true },
    );
  };

  const closeAccordionContent = (content) => {
    content.style.overflow = 'hidden';
    content.style.maxHeight = `${content.scrollHeight}px`;

    requestAnimationFrame(() => {
      content.style.maxHeight = '0px';
    });
  };

  const updateIcon = (type, iconElement, iconPlus, iconMinus, isOpen) => {
    if (type === 'chevron' && iconElement) {
      if (isOpen) {
        iconElement.classList.add('rotate-180');
      } else {
        iconElement.classList.remove('rotate-180');
      }
      return;
    }

    if (iconPlus) {
      if (isOpen) {
        iconPlus.classList.add('hidden');
      } else {
        iconPlus.classList.remove('hidden');
      }
    }

    if (iconMinus) {
      if (isOpen) {
        iconMinus.classList.remove('hidden');
      } else {
        iconMinus.classList.add('hidden');
      }
    }
  };

  const toggleFocusable = (container, isOpen) => {
    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const elements = container.querySelectorAll(selectors);

    for (const element of elements) {
      if (isOpen) {
        const original = element.dataset.originalTabindex;
        if (original !== undefined) {
          if (original === 'null' || original === '') {
            element.removeAttribute('tabindex');
          } else {
            element.setAttribute('tabindex', original);
          }
          delete element.dataset.originalTabindex;
        }
        continue;
      }

      const currentTabindex = element.getAttribute('tabindex');
      element.dataset.originalTabindex = currentTabindex ?? 'null';
      element.setAttribute('tabindex', '-1');
    }
  };

  const initAccordions = () => {
    const accordions = document.querySelectorAll('[data-accordion]');

    for (const accordion of accordions) {
      const trigger = accordion.querySelector('[data-accordion-trigger]');
      const content = accordion.querySelector('[data-accordion-content]');
      const iconElement = accordion.querySelector('[data-accordion-icon]');

      if (!trigger || !content) continue;
      if (initializedTriggers.has(trigger)) continue;
      initializedTriggers.add(trigger);

      const iconType = iconElement?.dataset.accordionIconType ?? 'plus-minus';
      const iconPlus = accordion.querySelector('[data-accordion-icon-plus]');
      const iconMinus = accordion.querySelector('[data-accordion-icon-minus]');
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        content.style.maxHeight = 'none';
        content.style.overflow = 'visible';
        updateIcon(iconType, iconElement, iconPlus, iconMinus, true);
      } else {
        content.style.maxHeight = '0px';
        updateIcon(iconType, iconElement, iconPlus, iconMinus, false);
        toggleFocusable(content, false);
      }

      trigger.addEventListener('click', () => {
        const isCurrentlyOpen = trigger.getAttribute('aria-expanded') === 'true';
        const newState = !isCurrentlyOpen;

        trigger.setAttribute('aria-expanded', newState);
        content.setAttribute('aria-hidden', !newState);
        toggleFocusable(content, newState);
        updateIcon(iconType, iconElement, iconPlus, iconMinus, newState);

        if (newState) {
          openAccordionContent(content);
        } else {
          closeAccordionContent(content);
        }
      });
    }
  };

  document.addEventListener('DOMContentLoaded', initAccordions);

  // Coalesced into one pass per frame: a section morph fires many childList
  // records, and each one used to trigger a full document re-scan.
  let scanQueued = false;
  const accordionObserver = new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;

    requestAnimationFrame(() => {
      scanQueued = false;
      initAccordions();
    });
  });

  accordionObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
